import React, { useRef, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Pause, Play } from "iconsax-react";

export default function ExploreVideo({ eyebrow, videoUrl, features }) {
  const videoRef = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(true);

  const activeIndex = features.findIndex((f) => time >= f.start && time < f.end);
  const active = activeIndex >= 0 ? features[activeIndex] : null;

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  const seekTo = (feature) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = feature.start;
    video.play();
    setPlaying(true);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "70vh", md: "100vh" },
        overflow: "hidden",
        backgroundColor: "#05141f",
      }}
    >
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      >
        <source src={videoUrl} type="video/mp4" />
      </Box>

      {/* Feature card for the segment currently playing */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, md: "12%" },
          right: { xs: 16, md: "8%" },
          left: { xs: 16, md: "auto" },
          maxWidth: { md: 380 },
          p: { xs: 2, md: 3 },
          color: "#fff",
          backgroundColor: "rgba(5, 20, 31, 0.7)",
          backdropFilter: "blur(6px)",
          opacity: active ? 1 : 0,
          transform: active ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          pointerEvents: "none",
        }}
      >
        <Typography
          letterSpacing={2}
          fontFamily="KIAbodyLatinbasic_Bold"
          sx={{ fontSize: "12px", color: "#FFFFFFB3", mb: 1 }}
        >
          {eyebrow}
        </Typography>
        <Typography
          fontFamily="KIAbodyLatinbasic_Bold"
          sx={{ fontSize: "clamp(16px, 2vw, 22px)", lineHeight: 1.3, mb: 1 }}
        >
          {active?.label}
        </Typography>
        <Typography
          fontFamily="KIAbodyLatinbasic_Regular"
          sx={{ fontSize: "clamp(13px, 1.4vw, 15px)", lineHeight: 1.5 }}
        >
          {active?.description}
        </Typography>
      </Box>

      {/* Timeline */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          px: { xs: 2, md: 6 },
          pb: { xs: 2, md: 4 },
          pt: 6,
          background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
        }}
      >
        <IconButton
          onClick={togglePlay}
          aria-label={playing ? "Pause video" : "Play video"}
          sx={{ border: "1px solid #FFFFFF80", color: "#fff", flexShrink: 0 }}
        >
          {playing ? <Pause size={18} color="#fff" /> : <Play size={18} color="#fff" />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "space-between",
              gap: 2,
              mb: 1.5,
            }}
          >
            {features.map((f, i) => (
              <Typography
                key={f.label}
                component="button"
                onClick={() => seekTo(f)}
                fontFamily="KIAbodyLatinbasic_Regular"
                sx={{
                  flex: 1,
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  fontSize: "12px",
                  lineHeight: 1.4,
                  color: i === activeIndex ? "#fff" : "#FFFFFF80",
                  transition: "color 0.3s",
                }}
              >
                {f.label}
              </Typography>
            ))}
          </Box>

          <Box sx={{ position: "relative", height: 2, backgroundColor: "#FFFFFF40" }}>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: duration ? `${(time / duration) * 100}%` : 0,
                backgroundColor: "#fff",
              }}
            />
            {duration > 0 &&
              features.map((f) => (
                <Box
                  key={f.label}
                  onClick={() => seekTo(f)}
                  sx={{
                    position: "absolute",
                    top: -4,
                    left: `${(f.start / duration) * 100}%`,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#BB162B",
                    cursor: "pointer",
                  }}
                />
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
